import { normalizeReferralCode } from './referralCode';

/**
 * The code resolved from a v2 referral link, held until sign-up reads it.
 *
 * It has to survive the portal's redirects (`/` and `*` both `<Navigate>` to
 * login with a bare URL) and a reload or back-navigation during the wizard, so
 * it lives in `localStorage`, not React state.
 *
 * **A new key, not `referralId`** (GOTU-7432): that key historically held a
 * legacy Mongo referral id, and an office who visited weeks ago with a legacy
 * link still has one sitting there. Reusing the key would post a Mongo id as a
 * v2 code. `useReferralHandler` clears the legacy key while the rebuild is on.
 *
 * Reads are synchronous so GOTU-7317 can prepopulate the field on first render.
 * Every access is guarded: a storage fault (private mode, blocked storage) must
 * degrade to "no code" and never throw into the render path.
 */
const PENDING_REFERRAL_CODE_KEY = 'referralPendingCode';

export const readPendingReferralCode = (): string | null => {
  try {
    const stored = localStorage.getItem(PENDING_REFERRAL_CODE_KEY);

    return normalizeReferralCode(stored) || null;
  } catch {
    return null;
  }
};

export const setPendingReferralCode = (code: string): void => {
  try {
    localStorage.setItem(PENDING_REFERRAL_CODE_KEY, normalizeReferralCode(code));
  } catch {
    // Best effort: losing the code costs the referral, not the page.
  }
};

export const clearPendingReferralCode = (): void => {
  try {
    localStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
  } catch {
    // Best effort, as above.
  }
};
